import {useState} from "react";
import {useLocale, useMessages} from "i18n";
import {recipientLimitLabel} from "pricing/planEntitlements";
import type {GroupResponse} from "contacts/groups/GroupApi";
import {nextUpgradePlan, type Plan} from "../gating";
import {
	recipientSelection, recipientSelectionState, replaceSelectedGroups, tryMergeRecipients, validateRecipientSelection,
	type RecipientSelection,
} from "./createFormLogic";
import {recipientLimitMessage} from "./recipientLimitMessage";
import {recipientUpgradeNudge, type RecipientUpgradeNudge} from "./recipientUpgradeNudge";

/** The unified recipients field (ui#210), as both of the home composer's directions use it. */
export interface RecipientsFieldVM {
	/** Direct email chips in the unified recipients field (ui#210). */
	recipients: string[];
	/** Selected group chips in the same unified field — they coexist with the email chips. */
	selectedGroups: GroupResponse[];
	/** Replaces the controlled group selection and reports whether its unique union was admitted. */
	selectGroups: (groups: GroupResponse[]) => boolean;
	removeGroup: (id: string) => void;
	recipientError: string | null;
	recipientCanUpgrade: boolean;
	recipInput: string;
	setRecipInput: (value: string) => void;
	commitRecipient: () => void;
	/** Merges a batch of addresses (the contacts picker's selection, ui#166) — dedups + caps. */
	addRecipients: (emails: string[]) => void;
	removeRecipient: (index: number) => void;
	recip: {
		limit: number;
		canAdd: boolean;
		nudge: boolean;
		placeholder: string;
		count: number;
		message: string | null;
		statusMessage: string | null;
		/**
		 * The inline offer beside a FULL field (ui#2524), or null when there is nothing to sell — the
		 * rule is {@link recipientUpgradeNudge}'s, so this is carried rather than decided here.
		 */
		upgrade: RecipientUpgradeNudge | null;
	};
}

/** The field's state as well as its VM: the composer that owns it reads the selection and replaces it. */
export interface RecipientsField extends RecipientsFieldVM {
	/** The chips as one selection (`recipientSelection`), derived on every render so the two cannot disagree. */
	selection: RecipientSelection;
	/** Whether the selection names at least one person and stays inside the plan's cap. */
	selectionValid: boolean;
	/** Replaces every chip with these addresses and clears the rest, e.g. a reply's seed (ui#1803). */
	seed: (emails: string[]) => void;
	/** Empties the field. */
	clear: () => void;
}

/**
 * The unified recipients field's state, lifted out of the send form so the request mode shares it
 * (kitecorp/transferify-ui#3140): email chips and group chips against ONE shared cap, the merge and
 * replace rules from `createFormLogic`, and the copy for a full or refused list. A thin adapter: every
 * rule it applies is a pure function.
 */
export function useRecipientsField(plan: Plan): RecipientsField {
	const {sender, pricing} = useMessages();
	const {locale} = useLocale();
	const [recipients, setRecipients] = useState<string[]>([]);
	const [selectedGroups, setSelectedGroups] = useState<GroupResponse[]>([]);
	const [recipientError, setRecipientError] = useState<string | null>(null);
	const [recipInput, setRecipInput] = useState("");

	const selection = recipientSelection(recipients, selectedGroups);
	const recipientCanUpgrade = nextUpgradePlan(plan) !== null;
	const limitMessage = recipientLimitMessage(
		plan,
		pricing.planNames,
		pricing.compare.unlimited,
		sender.recipients,
		locale,
	);
	const reachedMessage = sender.recipients.limitReached(
		pricing.planNames[plan],
		recipientLimitLabel(plan, pricing.compare.unlimited, locale),
	);

	// The unified recipients field (ui#210): email chips and group chips coexist, so neither entry
	// path clears the other — both merge against ONE shared recipient cap (their combined union).
	// A full list arms NO upsell wall (owner 2026-09-17, ui#2518): the sheet's own Upgrade action is
	// the single ask for this cap, so the card is not armed from either entry path.
	const mergeRecipients = (emails: string[]) => {
		const result = tryMergeRecipients(recipients, emails, selectedGroups, plan);
		setRecipients(result.recipients);
		setRecipientError(result.accepted ? null : limitMessage);
	};
	const commitRecipient = () => {
		mergeRecipients([recipInput]);
		setRecipInput("");
	};
	const addRecipients = (emails: string[]) => mergeRecipients(emails);
	const removeRecipient = (index: number) => {
		setRecipients(list => list.filter((_, i) => i !== index));
		setRecipientError(null);
	};
	const selectGroups = (groups: GroupResponse[]) => {
		const result = replaceSelectedGroups(selectedGroups, groups, recipients, plan);
		setSelectedGroups(result.groups);
		setRecipientError(result.accepted
			? null
			: result.reason === "empty" ? sender.recipients.emptyGroupSelection : limitMessage);
		return result.accepted;
	};
	const removeGroup = (id: string) => {
		setSelectedGroups(groups => groups.filter(group => group.id !== id));
		setRecipientError(null);
	};
	const seed = (emails: string[]) => {
		setRecipients(emails);
		setSelectedGroups([]);
		setRecipInput("");
		setRecipientError(null);
	};

	const recipientSelectionStatus = recipientSelectionState(selection, plan);
	const recipientAtLimit = recipientSelectionStatus.count >= recipientSelectionStatus.limit;
	const atLimitMessage = recipientCanUpgrade ? limitMessage : reachedMessage;
	const recipientStatusMessage = recipientError ?? (recipientAtLimit ? atLimitMessage : null);
	const recip = {
		limit: recipientSelectionStatus.limit,
		canAdd: !recipientAtLimit,
		nudge: (recipientAtLimit && recipientCanUpgrade) || recipientError !== null,
		count: recipientSelectionStatus.count,
		placeholder: recipients.length ? sender.recipients.addAnother : sender.recipients.emailPlaceholder,
		message: ((recipientAtLimit && recipientCanUpgrade) || recipientError !== null)
			? recipientError ?? limitMessage
			: null,
		statusMessage: recipientStatusMessage,
		upgrade: recipientUpgradeNudge(plan, recipientSelectionStatus.count),
	};

	return {
		recipients, selectedGroups, selectGroups, removeGroup, recipientError, recipientCanUpgrade, recipInput,
		setRecipInput, commitRecipient, addRecipients, removeRecipient, recip,
		selection,
		selectionValid: validateRecipientSelection(selection, plan).valid,
		seed,
		clear: () => seed([]),
	};
}
